import { Link, useLocation, useParams, useSearchParams } from 'react-router-dom';
import { Button, Dropdown, Empty, Input, Modal, Spin, Tooltip } from 'antd';
import {
    EditOutlined,
    HomeOutlined,
    MoreOutlined,
    SearchOutlined,
} from '@ant-design/icons';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { usePriceSimulatorV3Context } from 'contexts/PriceSimulatorV3Context';
import { colorConstants } from 'config/colorConstants';
import PriceSimulatorCreateEditModal from 'components/molecules/PPA/Modals/PriceSimulatorCreateEditModal';
import { PpaScenarioActionType } from 'types/ppa';
import { ppaSimulationUrls } from 'config/urls';
import { useCommonMutationHandler } from 'hooks/general/useCommonMutationHandler';
import { PricingSimulatorNotificationMessages } from 'utils/Enums';
import {
    formatMissingPpaScopeMessage,
    getPpaScopeKeyLabelsFromScenarioManagerMeta,
} from 'utils/ppaHelpers';
import { getAuthUserMail, sendNotification } from 'utils/helperMethods';
import { usePpaScenarioMetadataSummary } from 'hooks/ppa/usePpaScenarioMetadataSummary';
import ScenarioLensSection from 'components/molecules/PricingSimulatorV3/ScenarioLensSection/ScenarioLensSection';
import type { ScenarioLensSectionController } from 'components/molecules/PricingSimulatorV3/ScenarioLensSection/scenarioLens.types';
import SavedViewStateSelect from 'components/molecules/PricingSimulatorV3/SavedViewStateSelect';
import { isSavedViewLoadedInWorkspace } from 'components/molecules/PricingSimulatorV3/SavedViewStateSelect/savedViewStateSelect.helpers';
import SaveOnLeavePrompt from 'components/molecules/PricingSimulatorV3/SaveOnLeavePrompt';
import { scenarioDeleteConfirmIcon } from 'components/molecules/PricingSimulatorV3/config/scenarioActions';
import { useScenarioNavigationBlocker } from 'components/molecules/PricingSimulatorV3/hooks/useScenarioNavigationBlocker';
import type { SelectedViewFromList } from 'hooks/ppa/ppaViewPrefetch.helpers';
import type { PPAViewDataSource } from 'types/ppaViews';
import {
    ScenarioTabLabel,
    StyledNewScenarioTabButton,
    StyledScenarioMetadataSummary,
    StyledScenarioPanel,
    StyledScenarioTabs,
    StyledScenarioTabsRow,
} from './PriceSimulatorV3ProjectScenarios.styles';

const RESET_SCENARIO_MENU_KEY = 'reset_scenario';
const DUPLICATE_SCENARIO_MENU_KEY = 'duplicate_scenario';
const BULK_UPDATE_MENU_KEY = 'bulk_update';
const DOWNLOAD_TEMPLATE_MENU_KEY = 'download_template';
const UPLOAD_FILE_MENU_KEY = 'upload_file';
const DELETE_SCENARIO_MENU_KEY = 'delete_scenario';

type ScenarioViewSelection = {
    scenarioId: number | null;
    viewId: number | null;
    dataSource: PPAViewDataSource;
};

const Breadcrumb = ({
    basePath,
    projectName,
    scenarioName,
}: {
    basePath: string;
    projectName: string;
    scenarioName: string;
}) => (
    <nav className="flex items-center" aria-label="Breadcrumb">
        <Link to="/" className="flex items-center text-black/50" aria-label="Home">
            <HomeOutlined className="text-[12px]" />
        </Link>
        <span className="px-2 text-[10px] leading-[22px]" style={{ color: colorConstants.breadcrumbSlash }} aria-hidden>
            /
        </span>
        <Link to={basePath} className="text-[12px] leading-[22px] text-fontColor/70">
            Portfolio Planning &amp; Simulation
        </Link>
        <span className="px-2 text-[10px] leading-[22px]" style={{ color: colorConstants.breadcrumbSlash }} aria-hidden>
            /
        </span>
        <span className="text-[12px] leading-[22px] text-fontColor/70">{projectName}</span>
        <span className="px-2 text-[10px] leading-[22px]" style={{ color: colorConstants.breadcrumbSlash }} aria-hidden>
            /
        </span>
        <span className="text-[12px] font-medium leading-[22px] text-fontColor">Scenario: {scenarioName}</span>
    </nav>
);

const PriceSimulatorV3ProjectScenarios = () => {
    const location = useLocation();
    const { projectId: projectIdParam } = useParams<{ projectId: string }>();
    const [searchParams, setSearchParams] = useSearchParams();
    const {
        projects,
        scenariosByProject,
        scenarioCountByProject,
        loadingScenarioProjectIds,
        scenarioManagerMeta,
        fetchScenariosByProject,
        refreshScenariosForProject,
    } = usePriceSimulatorV3Context();
    const [scenarioActionModal, setScenarioActionModal] = useState<{
        actionType: PpaScenarioActionType;
        editParams?: Record<string, unknown>;
    } | null>(null);
    const [viewSelection, setViewSelection] = useState<ScenarioViewSelection>({
        scenarioId: null,
        viewId: null,
        dataSource: 'view',
    });
    const [isViewListReady, setIsViewListReady] = useState(false);
    const [isScenarioViewModified, setIsScenarioViewModified] = useState(false);
    const [isCustomViewLoaded, setIsCustomViewLoaded] = useState(false);
    const [loadedViewName, setLoadedViewName] = useState('');
    const [isLensContentLoading, setIsLensContentLoading] = useState(true);
    const [isViewDetached, setIsViewDetached] = useState(false);
    const [leavePromptOpen, setLeavePromptOpen] = useState(false);
    const [isLeaveActionSubmitting, setIsLeaveActionSubmitting] = useState(false);
    const lensControllerRef = useRef<ScenarioLensSectionController | null>(null);
    const pendingNavigationRef = useRef<(() => void) | null>(null);
    const scenarioMetadataSummary = usePpaScenarioMetadataSummary();

    const { mutate: deleteScenarioMutation } = useCommonMutationHandler({
        url: ppaSimulationUrls.deleteScenarioUrl,
        method: 'post',
    });

    const projectId = Number(projectIdParam);
    const scenarios = Number.isFinite(projectId) ? scenariosByProject[projectId] ?? [] : [];
    const scenarioCount = Number.isFinite(projectId) ? scenarioCountByProject[projectId] ?? scenarios.length : 0;
    const childLimit = scenarioManagerMeta?.child_limit;
    const isScenarioLimitReached = typeof childLimit === 'number' && scenarioCount >= childLimit;
    const isLoading = Number.isFinite(projectId) ? Boolean(loadingScenarioProjectIds[projectId]) : false;
    const project = projects.find((projectItem) => projectItem.project_id === projectId);
    const projectName = project?.project_name ?? `Project ${projectIdParam ?? ''}`;
    const selectedScenarioId = searchParams.get('scenarioId');
    const isCreateScenarioModalOpen = searchParams.get('createScenario') === '1';
    const basePath = useMemo(() => {
        const projectSegment = '/project/';
        const segmentIndex = location.pathname.indexOf(projectSegment);
        if (segmentIndex === -1) {
            return '/';
        }
        return location.pathname.slice(0, segmentIndex);
    }, [location.pathname]);

    useEffect(() => {
        if (!Number.isFinite(projectId)) {
            return;
        }
        void fetchScenariosByProject(projectId);
    }, [fetchScenariosByProject, projectId]);

    useEffect(() => {
        if (!scenarios.length) {
            return;
        }

        const hasSelectedScenario = selectedScenarioId
            ? scenarios.some((scenario) => String(scenario.scenario_id) === selectedScenarioId)
            : false;

        if (!selectedScenarioId || !hasSelectedScenario) {
            setSearchParams({ scenarioId: String(scenarios[0].scenario_id) }, { replace: true });
        }
    }, [scenarios, selectedScenarioId, setSearchParams]);

    const selectedScenario = useMemo(
        () =>
            scenarios.find((scenario) => String(scenario.scenario_id) === String(selectedScenarioId)) ??
            scenarios[0],
        [scenarios, selectedScenarioId],
    );

    const scenarioTabs = useMemo(
        () =>
            scenarios.map((scenario) => ({
                key: String(scenario.scenario_id),
                label: (
                    <ScenarioTabLabel title={scenario.scenario_name}>
                        {scenario.scenario_name}
                    </ScenarioTabLabel>
                ),
            })),
        [scenarios],
    );

    const updateSearchParams = (nextValues: Record<string, string | undefined>, replace = false) => {
        const nextParams = new URLSearchParams(searchParams);
        Object.entries(nextValues).forEach(([key, value]) => {
            if (value === undefined || value === '') {
                nextParams.delete(key);
            } else {
                nextParams.set(key, value);
            }
        });
        setSearchParams(nextParams, { replace });
    };

    const closeCreateScenarioModal = () => {
        updateSearchParams({ createScenario: undefined });
    };

    const openCreateScenarioModal = () => {
        updateSearchParams({ createScenario: '1' });
    };

    const selectedScenarioName = selectedScenario?.scenario_name ?? 'Scenario';
    const hasValidPpaScope = selectedScenario?.ppa_scope_id != null;
    const missingPpaScopeMessage = useMemo(
        () =>
            formatMissingPpaScopeMessage(
                getPpaScopeKeyLabelsFromScenarioManagerMeta(scenarioManagerMeta),
            ),
        [scenarioManagerMeta],
    );
    const isScenarioOwner = useMemo(() => {
        const owner = (selectedScenario?.created_by ?? '').trim().toLowerCase();
        const current = (getAuthUserMail() ?? '').trim().toLowerCase();
        return Boolean(owner && owner === current);
    }, [selectedScenario?.created_by]);
    const isCreateActionDisabled = !Number.isFinite(projectId) || !selectedScenario;
    const isDuplicateDisabled = isScenarioLimitReached || isCreateActionDisabled;
    const isDeleteDisabled = scenarios.length <= 1 || isCreateActionDisabled;

    const openRenameScenarioModal = useCallback(() => {
        if (!selectedScenario || !Number.isFinite(projectId)) {
            return;
        }
        setScenarioActionModal({
            actionType: PpaScenarioActionType.Rename,
            editParams: {
                scenario_id: selectedScenario.scenario_id,
                scenario_name: selectedScenario.scenario_name,
            },
        });
    }, [projectId, selectedScenario]);

    const openDuplicateScenarioModal = useCallback(() => {
        if (!selectedScenario || !Number.isFinite(projectId) || isScenarioLimitReached) {
            return;
        }
        setScenarioActionModal({
            actionType: PpaScenarioActionType.Duplicate,
            editParams: {
                scenario_name: `${selectedScenario.scenario_name}_copy`,
            },
        });
    }, [isScenarioLimitReached, projectId, selectedScenario]);

    const activeScenarioId = selectedScenario?.scenario_id ?? null;
    const resolvedViewId =
        viewSelection.scenarioId === activeScenarioId ? viewSelection.viewId : null;
    const resolvedDataSource =
        viewSelection.scenarioId === activeScenarioId ? viewSelection.dataSource : 'view';
    const isSavedViewLoaded = useMemo(
        () =>
            viewSelection.scenarioId === activeScenarioId &&
            isSavedViewLoadedInWorkspace(viewSelection),
        [activeScenarioId, viewSelection],
    );

    const handleSelectedViewChange = useCallback(
        (selection: SelectedViewFromList) => {
            if (selection.viewId != null) {
                setIsViewDetached(false);
            }

            setViewSelection((current) => {
                if (
                    current.scenarioId === activeScenarioId &&
                    current.viewId === selection.viewId &&
                    current.dataSource === selection.dataSource
                ) {
                    return current;
                }

                return {
                    scenarioId: activeScenarioId,
                    viewId: selection.viewId,
                    dataSource: selection.dataSource,
                };
            });
        },
        [activeScenarioId],
    );

    const handleViewListLoadingChange = useCallback((isLoading: boolean) => {
        setIsViewListReady(!isLoading);
    }, []);

    const handleLoadedViewChange = useCallback(
        (_loadedViewId: number | null, isCustomView: boolean, viewName?: string) => {
            setIsCustomViewLoaded(isCustomView);
            setLoadedViewName(viewName?.trim() ?? '');
        },
        [],
    );

    useEffect(() => {
        setIsScenarioViewModified(false);
        setIsCustomViewLoaded(false);
        setLoadedViewName('');
        setIsLensContentLoading(true);
        setIsViewListReady(false);
        setIsViewDetached(false);
        setViewSelection((current) =>
            current.scenarioId === activeScenarioId
                ? current
                : {
                      scenarioId: activeScenarioId,
                      viewId: null,
                      dataSource: 'view',
                  },
        );
    }, [activeScenarioId]);

    const openLeavePrompt = useCallback((proceed: () => void) => {
        pendingNavigationRef.current = proceed;
        setLeavePromptOpen(true);
    }, []);

    const closeLeavePrompt = useCallback(() => {
        pendingNavigationRef.current = null;
        setLeavePromptOpen(false);
        setIsLeaveActionSubmitting(false);
    }, []);

    const completePendingNavigation = useCallback(() => {
        const proceed = pendingNavigationRef.current;
        pendingNavigationRef.current = null;
        setLeavePromptOpen(false);
        setIsLeaveActionSubmitting(false);
        setIsScenarioViewModified(false);
        proceed?.();
    }, []);

    const runLeaveAction = useCallback(
        async (action: () => Promise<void>) => {
            setIsLeaveActionSubmitting(true);
            try {
                await action();
                completePendingNavigation();
            } catch {
                setIsLeaveActionSubmitting(false);
            }
        },
        [completePendingNavigation],
    );

    const handleLeaveUpdateMainView = useCallback(async () => {
        if (!lensControllerRef.current) {
            throw new Error('Lens controller unavailable');
        }
        await lensControllerRef.current.saveScenarioAndUpdateView();
    }, []);

    const handleLeaveIgnore = useCallback(async () => {
        if (!lensControllerRef.current) {
            throw new Error('Lens controller unavailable');
        }
        await lensControllerRef.current.saveScenarioView();
    }, []);

    const handleLeaveDiscard = useCallback(async () => {
        if (!lensControllerRef.current) {
            throw new Error('Lens controller unavailable');
        }
        await lensControllerRef.current.resetToPersisted();
    }, []);

    const handleViewLoaded = useCallback(() => {
        setIsScenarioViewModified(false);
        setIsViewDetached(false);
    }, []);

    const handlePersistScenarioSnapshot = useCallback(async () => {
        if (!lensControllerRef.current) {
            throw new Error('Lens controller unavailable');
        }

        await lensControllerRef.current.saveScenarioView();
    }, []);

    const shouldPromptOnLeave = isScenarioOwner && isScenarioViewModified && isSavedViewLoaded;
    const hasUnsavedScenarioDeviation =
        isScenarioOwner && isScenarioViewModified && !isSavedViewLoaded;

    const persistScenarioDeviationIfNeeded = useCallback(
        async (proceed: () => void) => {
            if (!hasUnsavedScenarioDeviation) {
                proceed();
                return;
            }

            try {
                await handlePersistScenarioSnapshot();
                setIsScenarioViewModified(false);
                proceed();
            } catch {
                sendNotification('error', 'Failed to save scenario changes. Please try again.');
            }
        },
        [handlePersistScenarioSnapshot, hasUnsavedScenarioDeviation],
    );

    useScenarioNavigationBlocker(shouldPromptOnLeave || hasUnsavedScenarioDeviation, ({ proceed }) => {
        if (shouldPromptOnLeave) {
            openLeavePrompt(proceed);
            return;
        }

        void persistScenarioDeviationIfNeeded(proceed);
    });

    const handleScenarioTabChange = useCallback(
        (nextTabKey: string) => {
            const proceed = () => updateSearchParams({ scenarioId: nextTabKey });

            if (shouldPromptOnLeave) {
                openLeavePrompt(proceed);
                return;
            }

            void persistScenarioDeviationIfNeeded(proceed);
        },
        [openLeavePrompt, persistScenarioDeviationIfNeeded, shouldPromptOnLeave, updateSearchParams],
    );

    const handleNewScenarioClick = useCallback(() => {
        if (isScenarioLimitReached) {
            return;
        }

        const proceed = () => openCreateScenarioModal();

        if (shouldPromptOnLeave) {
            openLeavePrompt(proceed);
            return;
        }

        void persistScenarioDeviationIfNeeded(proceed);
    }, [
        isScenarioLimitReached,
        openCreateScenarioModal,
        openLeavePrompt,
        persistScenarioDeviationIfNeeded,
        shouldPromptOnLeave,
    ]);

    const handleScenarioExitComplete = useCallback(() => {
        setIsScenarioViewModified(false);
    }, []);

    const handleExitViewRequested = useCallback((completeExit: () => void) => {
        completeExit();
        setIsViewDetached(true);
    }, []);

    const handleUpdateLoadedView = useCallback(async () => {
        if (!isScenarioOwner || !lensControllerRef.current) {
            return;
        }

        await lensControllerRef.current.saveScenarioAndUpdateView();
        setIsScenarioViewModified(false);
    }, [isScenarioOwner]);

    const showScenarioWorkspaceLoader = !isViewListReady || isLensContentLoading;

    const handleLensContentLoadingChange = useCallback((isLoading: boolean) => {
        setIsLensContentLoading(isLoading);
    }, []);

    const closeScenarioActionModal = useCallback(() => {
        setScenarioActionModal(null);
    }, []);

    const confirmDeleteScenario = useCallback(() => {
        if (!selectedScenario || !Number.isFinite(projectId) || scenarios.length <= 1) {
            return;
        }

        Modal.confirm({
            title: 'Delete Scenario',
            content: 'Are you sure you want to delete this scenario?',
            icon: scenarioDeleteConfirmIcon,
            className: 'price-simulator-v3-delete-scenario-modal',
            centered: true,
            okText: 'Delete',
            cancelText: 'Cancel',
            okButtonProps: { danger: true, style: { borderRadius: 8 } },
            cancelButtonProps: { style: { borderRadius: 8 } },
            onOk: () =>
                new Promise<void>((resolve, reject) => {
                    const payload: Record<string, unknown> = {
                        scenario_id: selectedScenario.scenario_id,
                    };
                    const createdBy = getAuthUserMail();
                    if (createdBy) {
                        payload.created_by = createdBy;
                    }

                    deleteScenarioMutation(payload, {
                        onSuccess: async () => {
                            await refreshScenariosForProject(projectId);
                            sendNotification('success', 'Scenario deleted successfully');
                            updateSearchParams({ scenarioId: undefined });
                            resolve();
                        },
                        onError: (error: any) => {
                            const errorMessage =
                                String(error?.response?.data?.detail || '').trim() ||
                                PricingSimulatorNotificationMessages.SCENARIO_ACTION_ERROR;
                            sendNotification('error', errorMessage);
                            reject(error);
                        },
                    });
                }),
        });
    }, [deleteScenarioMutation, projectId, refreshScenariosForProject, scenarios.length, selectedScenario]);

    const moreActionItems = useMemo(
        () => [
            {
                key: RESET_SCENARIO_MENU_KEY,
                label: 'Reset Scenario',
                disabled: true,
            },
            {
                key: DUPLICATE_SCENARIO_MENU_KEY,
                label: 'Duplicate Scenario',
                disabled: isDuplicateDisabled || !isScenarioOwner,
            },
            {
                key: BULK_UPDATE_MENU_KEY,
                label: 'Bulk Update',
                disabled: true,
                children: [
                    { key: DOWNLOAD_TEMPLATE_MENU_KEY, label: 'Download Template', disabled: true },
                    { key: UPLOAD_FILE_MENU_KEY, label: 'Upload File', disabled: true },
                ],
            },
            {
                type: 'divider' as const,
            },
            {
                key: DELETE_SCENARIO_MENU_KEY,
                label: 'Delete Scenario',
                danger: true,
                disabled: isDeleteDisabled || !isScenarioOwner,
            },
        ],
        [isDeleteDisabled, isDuplicateDisabled, isScenarioOwner],
    );

    const handleMoreMenuAction = useCallback(
        ({ key }: { key: string }) => {
            if (!isScenarioOwner) {
                return;
            }
            if (key === DUPLICATE_SCENARIO_MENU_KEY) {
                openDuplicateScenarioModal();
                return;
            }
            if (key === DELETE_SCENARIO_MENU_KEY) {
                confirmDeleteScenario();
            }
        },
        [confirmDeleteScenario, isScenarioOwner, openDuplicateScenarioModal],
    );

    return (
        <div className="flex min-h-full flex-col gap-2 ">
            <div className="flex flex-wrap items-center justify-between gap-3 pb-5 py-4">
                <Breadcrumb
                    basePath={basePath}
                    projectName={projectName}
                    scenarioName={selectedScenarioName}
                />
                {!isLoading && scenarioTabs.length ? (
                    <StyledScenarioMetadataSummary>
                        {scenarioMetadataSummary.fields.map((field) => (
                            <span key={field.label}>
                                <span className="metadata-label">{field.label}:</span>
                                <span className="metadata-value">{field.value}</span>
                            </span>
                        ))}
                    </StyledScenarioMetadataSummary>
                ) : null}
            </div>
            <div className="flex min-h-[calc(100vh-150px)] flex-col rounded-xl bg-white p-4 pt-0">
                <PriceSimulatorCreateEditModal
                    open={isCreateScenarioModalOpen}
                    formKind="scenario"
                    projectId={Number.isFinite(projectId) ? projectId : undefined}
                    scenarioActionType={PpaScenarioActionType.Create}
                    existingScenarios={scenarios}
                    onCancel={closeCreateScenarioModal}
                    onSubmitFinish={(id) => {
                        const nextScenarioId =
                            typeof id === 'number' || typeof id === 'string' ? String(id) : null;
                        updateSearchParams(
                            {
                                createScenario: undefined,
                                scenarioId: nextScenarioId ?? selectedScenarioId ?? undefined,
                            },
                            true,
                        );
                        if (Number.isFinite(projectId)) {
                            void refreshScenariosForProject(projectId);
                        }
                    }}
                />
                <PriceSimulatorCreateEditModal
                    open={scenarioActionModal != null}
                    formKind="scenario"
                    projectId={Number.isFinite(projectId) ? projectId : undefined}
                    scenarioActionType={scenarioActionModal?.actionType ?? PpaScenarioActionType.Rename}
                    editParams={scenarioActionModal?.editParams}
                    existingScenarios={scenarios}
                    onCancel={closeScenarioActionModal}
                    onSubmitFinish={(id) => {
                        closeScenarioActionModal();
                        const nextScenarioId =
                            typeof id === 'number' || typeof id === 'string'
                                ? String(id)
                                : selectedScenarioId ?? undefined;
                        if (Number.isFinite(projectId)) {
                            void refreshScenariosForProject(projectId).then(() => {
                                updateSearchParams({ scenarioId: nextScenarioId }, true);
                            });
                            return;
                        }
                        updateSearchParams({ scenarioId: nextScenarioId }, true);
                    }}
                />
                {isLoading ? (
                    <div className="flex min-h-[280px] items-center justify-center">
                        <Spin />
                    </div>
                ) : scenarioTabs.length ? (
                    <div className="flex flex-1 flex-col">
                        <StyledScenarioTabsRow>
                            <StyledScenarioTabs
                                type="card"
                                size="small"
                                activeKey={selectedScenarioId ?? scenarioTabs[0]?.key}
                                items={scenarioTabs}
                                onChange={handleScenarioTabChange}
                            />
                            {!isScenarioLimitReached ? (
                                <StyledNewScenarioTabButton type="button" onClick={handleNewScenarioClick}>
                                    + New Scenario
                                </StyledNewScenarioTabButton>
                            ) : null}
                        </StyledScenarioTabsRow>
                        <StyledScenarioPanel className="flex flex-1 flex-col border-x border-t border-borderLinePurpuleAlt px-3 pt-3">
                            <div className="flex items-center justify-between gap-4   pb-5">
                                <div className="flex items-center gap-2 text-blackTitle">
                                    <span className="text-[18px] leading-[40px] font-medium tracking-[0.1px]">
                                        {selectedScenarioName}
                                    </span>
                                    {isScenarioOwner ? (
                                        <button
                                            type="button"
                                            onClick={openRenameScenarioModal}
                                            className="flex items-center justify-center rounded p-1 hover:bg-backgroundGreyAlt transition-colors"
                                            aria-label="Rename scenario"
                                        >
                                            <EditOutlined className="text-fontColor/60" />
                                        </button>
                                    ) : null}
                                </div>
                                <div className="flex items-center gap-2">
                                    <Button className="toolbar-btn !flex !items-center !gap-2 !px-2" icon={<AnalyticsIconButton />}>
                                        Analytics
                                    </Button>
                                    <SavedViewStateSelect
                                        scenarioId={selectedScenario?.scenario_id}
                                        ppaScopeId={selectedScenario?.ppa_scope_id ?? null}
                                        appliedScope={selectedScenario?.scope}
                                        isScenarioModified={isScenarioViewModified}
                                        isScenarioOwner={isScenarioOwner}
                                        getLiveViewConfig={() => lensControllerRef.current?.getLiveViewConfig() ?? null}
                                        onViewListLoadingChange={handleViewListLoadingChange}
                                        onSelectedViewChange={handleSelectedViewChange}
                                        onLoadedViewChange={handleLoadedViewChange}
                                        onViewLoaded={handleViewLoaded}
                                        onUpdateLoadedView={handleUpdateLoadedView}
                                        onPersistScenarioSnapshot={
                                            isScenarioOwner ? handlePersistScenarioSnapshot : undefined
                                        }
                                        onRequestUnsavedLeave={isScenarioOwner ? openLeavePrompt : undefined}
                                        onExitViewRequested={
                                            isScenarioOwner ? undefined : handleExitViewRequested
                                        }
                                        onScenarioExitComplete={handleScenarioExitComplete}
                                    />
                                    <Input
                                        placeholder="Search"
                                        suffix={<SearchOutlined className="text-fontColor/50" />}
                                        className="toolbar-search !w-[165px]"
                                    />
                                    <Button className="toolbar-btn gap-2 !px-4" icon={<FilterIconButton />}>
                                        Filter
                                    </Button>
                                    <Button className="toolbar-icon-btn" icon={<ReloadIconButton />} />
                                    <Button className="toolbar-icon-btn" icon={<DownloadIconButton />} />
                                    <Button
                                        className="toolbar-btn !text-fontColor/40 gap-2"
                                        icon={<SimulateIconButton />}
                                        disabled
                                    >
                                        Simulate
                                    </Button>
                                    <Tooltip
                                        title={
                                            isScenarioLimitReached
                                                ? `You can only create ${childLimit} scenarios`
                                                : ''
                                        }
                                    >
                                        <Dropdown
                                            trigger={['click']}
                                            menu={{
                                                items: moreActionItems,
                                                onClick: handleMoreMenuAction,
                                            }}
                                        >
                                            <Button
                                                className="toolbar-icon-btn"
                                                icon={<MoreOutlined />}
                                            />
                                        </Dropdown>
                                    </Tooltip>
                                </div>
                            </div>
                            <div className="relative flex min-h-[360px] flex-1 flex-col">
                                {showScenarioWorkspaceLoader ? (
                                    <div className="absolute inset-0 z-10 flex items-center justify-center bg-white">
                                        <Spin size="small" />
                                    </div>
                                ) : null}
                                <div className="flex h-[30px] items-center bg-[#F4F4F5] ps-5 text-[13px] text-fontColor/60  rounded-md ">
                                    No filters applied. Apply filters to see them here.
                                </div>
                                <div className="flex min-h-[320px] flex-1 flex-col pt-[8px]">
                                    {!hasValidPpaScope ? (
                                        <div className="flex min-h-[280px] flex-1 items-center justify-center rounded-md bg-backgroundGreyAlt px-6 text-center">
                                            <Empty description={missingPpaScopeMessage} />
                                        </div>
                                    ) : (
                                        <ScenarioLensSection
                                            key={`${selectedScenario?.scenario_id ?? 'none'}`}
                                            scenarioName={selectedScenarioName}
                                            scenarioId={selectedScenario?.scenario_id}
                                            viewId={resolvedViewId}
                                            dataSource={resolvedDataSource}
                                            isViewDetached={isViewDetached}
                                            viewListReady={isViewListReady}
                                            isOwner={isScenarioOwner}
                                            scenarioOwner={selectedScenario?.created_by ?? null}
                                            onDirtyChange={setIsScenarioViewModified}
                                            onContentLoadingChange={handleLensContentLoadingChange}
                                            lensControllerRef={lensControllerRef}
                                        />
                                    )}
                                </div>
                            </div>
                            <SaveOnLeavePrompt
                                open={leavePromptOpen}
                                viewName={loadedViewName}
                                canUpdateMainView={isCustomViewLoaded}
                                isSubmitting={isLeaveActionSubmitting}
                                onCancel={closeLeavePrompt}
                                onUpdateMainView={() => runLeaveAction(handleLeaveUpdateMainView)}
                                onIgnore={() => runLeaveAction(handleLeaveIgnore)}
                                onDiscard={() => runLeaveAction(handleLeaveDiscard)}
                            />
                        </StyledScenarioPanel>
                        <div className="mt-auto flex items-center justify-between bg-[#4CAF50] px-4 py-1.5 text-xs text-white">
                            <span>{isScenarioOwner ? 'Owner' : 'Viewing as reader'}</span>
                            {selectedScenario?.created_by && <span>Created by: {selectedScenario.created_by}</span>}
                        </div>
                    </div>
                ) : (
                    <div className="flex min-h-[280px] items-center justify-center rounded-md bg-backgroundGreyAlt">
                        <Empty description="No scenarios found for this project" />
                    </div>
                )}
            </div>
        </div>
    );
};

export default PriceSimulatorV3ProjectScenarios;

const AnalyticsIconButton = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
  <path fill-rule="evenodd" clip-rule="evenodd" d="M5.17251 1.33325H10.8276C11.3642 1.33324 11.8071 1.33324 12.1679 1.36271C12.5427 1.39333 12.8871 1.45904 13.2107 1.6239C13.7124 1.87956 14.1204 2.28751 14.3761 2.78928C14.5409 3.11283 14.6066 3.4573 14.6372 3.83204C14.6667 4.19283 14.6667 4.63573 14.6667 5.17237V10.8275C14.6667 11.3641 14.6667 11.807 14.6372 12.1678C14.6066 12.5425 14.5409 12.887 14.3761 13.2106C14.1204 13.7123 13.7124 14.1203 13.2107 14.3759C12.8871 14.5408 12.5427 14.6065 12.1679 14.6371C11.8071 14.6666 11.3642 14.6666 10.8276 14.6666H5.17249C4.63585 14.6666 4.19295 14.6666 3.83216 14.6371C3.45743 14.6065 3.11296 14.5408 2.7894 14.3759C2.28763 14.1203 1.87969 13.7123 1.62402 13.2106C1.45916 12.887 1.39345 12.5425 1.36284 12.1678C1.34409 11.9383 1.33727 11.6757 1.33479 11.3768C1.33311 11.3509 1.33293 11.325 1.33424 11.2992C1.33337 11.1507 1.33337 10.9936 1.33337 10.8275V5.17239C1.33337 4.63574 1.33336 4.19283 1.36284 3.83204C1.39345 3.4573 1.45916 3.11283 1.62402 2.78928C1.87969 2.28751 2.28763 1.87956 2.7894 1.6239C3.11296 1.45904 3.45743 1.39333 3.83216 1.36271C4.19295 1.33324 4.63586 1.33324 5.17251 1.33325ZM2.67134 11.6048C2.67483 11.7786 2.68097 11.9274 2.69174 12.0592C2.71562 12.3515 2.75891 12.501 2.81203 12.6052C2.93986 12.8561 3.14384 13.0601 3.39472 13.1879C3.49899 13.2411 3.64846 13.2843 3.94073 13.3082C4.24079 13.3327 4.62899 13.3333 5.20004 13.3333H10.8C11.3711 13.3333 11.7593 13.3327 12.0593 13.3082C12.3516 13.2843 12.5011 13.2411 12.6054 13.1879C12.8562 13.0601 13.0602 12.8561 13.188 12.6052C13.2412 12.501 13.2845 12.3515 13.3083 12.0592C13.3329 11.7592 13.3334 11.371 13.3334 10.7999V5.19992C13.3334 4.62887 13.3329 4.24067 13.3083 3.94061C13.2845 3.64833 13.2412 3.49887 13.188 3.3946C13.0602 3.14372 12.8562 2.93974 12.6054 2.81191C12.5011 2.75878 12.3516 2.7155 12.0593 2.69162C11.7593 2.6671 11.3711 2.66659 10.8 2.66659H5.20004C4.62899 2.66659 4.24079 2.6671 3.94073 2.69162C3.64846 2.7155 3.49899 2.75878 3.39472 2.81191C3.14384 2.93974 2.93986 3.14372 2.81203 3.3946C2.75891 3.49887 2.71562 3.64833 2.69174 3.94061C2.66723 4.24067 2.66671 4.62887 2.66671 5.19992V9.72378L4.48485 7.90564C4.48843 7.90205 4.49207 7.89841 4.49575 7.89473C4.55188 7.83855 4.61892 7.77147 4.68282 7.71722C4.75687 7.65436 4.86927 7.57066 5.02436 7.52027C5.2252 7.45501 5.44155 7.45501 5.64239 7.52027C5.79748 7.57066 5.90988 7.65436 5.98393 7.71722C6.04783 7.77147 6.11487 7.83856 6.171 7.89473C6.17468 7.89842 6.17832 7.90205 6.1819 7.90564L7.33337 9.05711L9.7239 6.66659H8.66671C8.29852 6.66659 8.00004 6.36811 8.00004 5.99992C8.00004 5.63173 8.29852 5.33325 8.66671 5.33325H11.3334C11.7016 5.33325 12 5.63173 12 5.99992V8.66659C12 9.03478 11.7016 9.33325 11.3334 9.33325C10.9652 9.33325 10.6667 9.03478 10.6667 8.66659V7.60939L8.1819 10.0942C8.17832 10.0978 8.17468 10.1014 8.171 10.1051C8.11487 10.1613 8.04783 10.2284 7.98393 10.2826C7.90988 10.3455 7.79748 10.4292 7.64239 10.4796C7.44155 10.5448 7.2252 10.5448 7.02436 10.4796C6.86927 10.4292 6.75687 10.3455 6.68282 10.2826C6.61892 10.2284 6.55188 10.1613 6.49575 10.1051C6.49207 10.1014 6.48843 10.0978 6.48485 10.0942L5.33337 8.94273L2.67134 11.6048Z" fill="#71717B"/>
</svg>
)

const ReloadIconButton = () => (
        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
  <path fill-rule="evenodd" clip-rule="evenodd" d="M7.33333 1.33333C5.85973 1.33333 4.52677 1.93009 3.56079 2.89674C3.05564 3.40224 2.48201 4.08752 2.02053 4.66667H4.66667C5.03486 4.66667 5.33333 4.96514 5.33333 5.33333C5.33333 5.70152 5.03486 6 4.66667 6H0.666667C0.298477 6 0 5.70152 0 5.33333V1.33333C0 0.965143 0.298477 0.666667 0.666667 0.666667C1.03486 0.666667 1.33333 0.965143 1.33333 1.33333V3.39629C1.73459 2.90832 2.19123 2.38097 2.61766 1.95425C3.82348 0.747596 5.49174 0 7.33333 0C11.0152 0 14 2.98477 14 6.66667C14 10.3486 11.0152 13.3333 7.33333 13.3333C4.29303 13.3333 1.72967 11.2987 0.927313 8.51817C0.825232 8.16441 1.02926 7.79488 1.38301 7.6928C1.73677 7.59072 2.1063 7.79474 2.20838 8.1485C2.85047 10.3737 4.90284 12 7.33333 12C10.2789 12 12.6667 9.61218 12.6667 6.66667C12.6667 3.72115 10.2789 1.33333 7.33333 1.33333Z" fill="#71717B"/>
</svg>
)

const DownloadIconButton = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
  <path fill-rule="evenodd" clip-rule="evenodd" d="M6.66667 0C7.03486 0 7.33333 0.298477 7.33333 0.666667V8.39052L10.1953 5.5286C10.4556 5.26825 10.8777 5.26825 11.1381 5.5286C11.3984 5.78894 11.3984 6.21105 11.1381 6.4714L7.13807 10.4714C6.87772 10.7318 6.45561 10.7318 6.19526 10.4714L2.19526 6.4714C1.93491 6.21105 1.93491 5.78894 2.19526 5.5286C2.45561 5.26825 2.87772 5.26825 3.13807 5.5286L6 8.39052V0.666667C6 0.298477 6.29848 0 6.66667 0ZM0 12.6667C0 12.2985 0.298477 12 0.666667 12H12.6667C13.0349 12 13.3333 12.2985 13.3333 12.6667C13.3333 13.0349 13.0349 13.3333 12.6667 13.3333H0.666667C0.298477 13.3333 0 13.0349 0 12.6667Z" fill="#71717B"/>
</svg>
)

const FilterIconButton = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="11" viewBox="0 0 14 11" fill="none">
  <path fill-rule="evenodd" clip-rule="evenodd" d="M10.6667 1.33333C9.93029 1.33333 9.33333 1.93029 9.33333 2.66667C9.33333 3.40305 9.93029 4 10.6667 4C11.403 4 12 3.40305 12 2.66667C12 1.93029 11.403 1.33333 10.6667 1.33333ZM8.08401 2C8.38004 0.849849 9.42411 0 10.6667 0C12.1394 0 13.3333 1.19391 13.3333 2.66667C13.3333 4.13943 12.1394 5.33333 10.6667 5.33333C9.42411 5.33333 8.38004 4.48349 8.08401 3.33333L0.666667 3.33333C0.298477 3.33333 0 3.03486 0 2.66667C3.97364e-08 2.29848 0.298477 2 0.666667 2L8.08401 2ZM2.66667 6.66667C1.93029 6.66667 1.33333 7.26362 1.33333 8C1.33333 8.73638 1.93029 9.33333 2.66667 9.33333C3.40305 9.33333 4 8.73638 4 8C4 7.26362 3.40305 6.66667 2.66667 6.66667ZM0 8C0 6.52724 1.19391 5.33333 2.66667 5.33333C3.90923 5.33333 4.95329 6.18318 5.24932 7.33333L12.6667 7.33333C13.0349 7.33333 13.3333 7.63181 13.3333 8C13.3333 8.36819 13.0349 8.66667 12.6667 8.66667L5.24932 8.66667C4.95329 9.81682 3.90923 10.6667 2.66667 10.6667C1.19391 10.6667 0 9.47276 0 8Z" fill="#71717B"/>
</svg>
)

const SimulateIconButton = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="11" height="14" viewBox="0 0 11 14" fill="none">
  <path d="M0.5 1.91358C0.5 1.26614 0.5 0.942417 0.634993 0.76397C0.752596 0.60851 0.932348 0.51231 1.12693 0.500692C1.35029 0.487356 1.61964 0.666924 2.15835 1.02606L9.16872 5.69964C9.61384 5.99639 9.83641 6.14477 9.91396 6.33178C9.98177 6.49528 9.98177 6.67904 9.91396 6.84254C9.83641 7.02956 9.61384 7.17793 9.16872 7.47468L2.15835 12.1483C1.61964 12.5074 1.35029 12.687 1.12693 12.6736C0.932348 12.662 0.752596 12.5658 0.634993 12.4104C0.5 12.2319 0.5 11.9082 0.5 11.2607V1.91358Z" stroke="#B8B8B8" stroke-linecap="round" stroke-linejoin="round"/>
</svg>
)